/**
 * First-party ad attribution for the checkout.
 *
 * The landing site forwards the ad click params (gclid, fbclid, utm_*) on the
 * CTA link to this domain. This module captures them from the URL on first
 * load, remembers them in THIS origin's localStorage (the landing site's
 * storage doesn't cross the subdomain), and produces a compact ad_source
 * string that is written onto every leads row — so the database itself can
 * split leads and purchases by source, independent of any pixel or cookie.
 */

const KEY = "sjs_checkout_attribution";
const PARAMS = ["gclid", "fbclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function captureAttribution() {
  try {
    const search = new URLSearchParams(window.location.search);
    const fromUrl = {};
    PARAMS.forEach((key) => {
      const value = search.get(key);
      if (value) fromUrl[key] = value;
    });
    // URL wins over storage so a fresh ad click never inherits a stale one
    if (Object.keys(fromUrl).length > 0) {
      window.localStorage.setItem(KEY, JSON.stringify(fromUrl));
    }
  } catch {
    /* storage unavailable: attribution stays best-effort */
  }
}

function stored() {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Compact source string for the leads table, e.g.
 *   "google | c:brand-search | gclid:Cj0K..."
 *   "meta | c:ctwa-retarget | fbclid:IwAR..."
 *   "organic"
 * Click IDs are truncated — enough to spot-check against ad platforms without
 * bloating rows.
 */
export function adSourceString() {
  const a = stored();
  let source = "organic";
  if (a.gclid) source = "google";
  else if (a.fbclid) source = "meta";
  else if (a.utm_source) source = String(a.utm_source).toLowerCase();

  const parts = [source];
  if (a.utm_campaign) parts.push(`c:${a.utm_campaign}`);
  if (a.gclid) parts.push(`gclid:${String(a.gclid).slice(0, 24)}`);
  if (a.fbclid) parts.push(`fbclid:${String(a.fbclid).slice(0, 24)}`);
  return parts.join(" | ").slice(0, 250);
}
